/*
  Which documents/dash tiles can be loaded based on diagnosis/platform
  Some pages use multiple document types and these are defined as subTypes
  Secondary docs are supplied to pages, but their editing status doesn't affect the page
  NOTE: Order of these also defines in which order the tiles are loaded in dashboard (First commons then diagnosis based)
*/
export type LoadableDocConfig = {
  /** Name and route the for the page. Not allowed names: files, assets, relNotes */
  name: string;
  /** If more than one document type is required then specify all in this array */
  subTypes?: TDocumentType[];
  /**
   * Filtering rules for subTypes if we don't want to supply the page with all documents with the type.
   * Note that the document can also be undefined, as when cancelling the creation of the first document the status goes from defined to undefined
   */
  subTypesFiltering?: {
    [documentType in TDocumentType]?: (doc: TDocumentDataMap[documentType] | undefined) => boolean;
  };
  secondaryDocs?: TDocumentType[];
  title?: string;
  nonNavigable?: boolean;
};

export const defineDocConfig = <const T extends readonly LoadableDocConfig[]>(config: T) => {
  return config;
};

/** The fields of a {@link LoadableDocConfig} that carry document types. */
export type DocTypeField = 'subTypes' | 'secondaryDocs';

/**
 * Union of the elements of the (optional) array field(s) `K` of `T`.
 * Resolves to `never` when the field is missing, so entries such as `{ name: 'dbs' }` don't widen the result.
 */
type ArrayFieldValues<T, K extends PropertyKey> = T extends { [P in K]?: readonly (infer V)[] } ? V : never;

/**
 * All document types a single config entry pulls in: its `name` when that happens to be a document type,
 * plus the contents of its `subTypes` and `secondaryDocs`. Works on unions of entries as well.
 *
 * Narrow `F` (e.g. to `'subTypes'`) to leave secondary docs out.
 */
export type DocTypesOfConfig<C extends LoadableDocConfig, F extends DocTypeField = DocTypeField> =
  | Extract<C['name'], TDocumentType>
  | ArrayFieldValues<C, F>;

/**
 * Maps every unique `name` in `T` to the union of document types used under that name.
 *
 * `T` may be a single config (`typeof parkinsonDocumentTypes`) or a union of configs
 * (`(typeof documentTypesByPlatform)[Platform]`), in which case entries sharing a `name` are merged.
 */
export type DocTypesByName<T extends readonly LoadableDocConfig[], F extends DocTypeField = DocTypeField> = {
  [K in T[number]['name']]: DocTypesOfConfig<Extract<T[number], { name: K }>, F>;
};

/** Every document type used anywhere in `T`. */
export type UsedDocTypes<
  T extends readonly LoadableDocConfig[],
  F extends DocTypeField = DocTypeField,
> = DocTypesOfConfig<T[number], F>;
